import React, { HTMLAttributes, useMemo } from 'react';
import { cn } from '@/utils';
import { Icon, TextContent } from '@/components';
import type { TIconName } from '@/design-tokens';
import type { VariantProps } from 'class-variance-authority';
import {
  cardIconVariants,
  cardIconContainerVariants,
  checkIconVariants,
  selectableCardVariants,
} from './SelectableCard.styles';

export interface SelectableCardProps
  extends
    Omit<HTMLAttributes<HTMLDivElement>, 'onSelect'>,
    VariantProps<typeof selectableCardVariants> {
  /** Label text (small text above the main text) */
  label?: string;
  /** Main text content */
  text: string;

  /**
   * Value to display on the right (e.g., "4/10")
   * If provided, it overrides `valuePrimary`, `valueSecondary`, and `delimiter`.
   */
  value?: string;
  /** Primary value to display left of the delimiter (e.g. "4" in "4/10") */
  valuePrimary?: string | number;
  /** Secondary value to display right of the delimiter (e.g. "10" in "4/10") */
  valueSecondary?: string | number;
  /** Delimiter to display between primary and secondary values (e.g. "/") */
  delimiter?: string;

  /** Visual variant of the card (success, warning, error) */
  variant?: 'default' | 'success' | 'warning' | 'error';

  /** Disable card interaction */
  disabled?: boolean;
  /** Whether the card is selected */
  selected?: boolean;

  /** Icon name to display on the left */
  icon?: TIconName;

  /** Callback when card is clicked */
  onSelect?: (selected: boolean) => void;
}

/**
 * SelectableCard Component
 *
 * A selectable card component with an icon, text content, value display,
 * and a radio/checkbox indicator. Based on the Figma design for filter cards.
 *
 * Features three variants: success (green), warning (amber), and error (red).
 * Each variant provides appropriate color theming for different states.
 */
export const SelectableCard: React.FC<SelectableCardProps> = ({
  className,
  delimiter = '/',
  disabled = false,
  icon = 'Medal',
  label = 'Filter by',
  onClick,
  onSelect,
  selected = false,
  text,
  value,
  valuePrimary,
  valueSecondary,
  variant = 'default',
  ...props
}) => {
  /** Convert primary value to string for display */
  const valueString = useMemo(() => {
    return value !== undefined ? value.toString() : undefined;
  }, [value]);

  /** Convert primary value to string for display */
  const valuePrimaryString = useMemo(() => {
    return valuePrimary !== undefined ? valuePrimary.toString() : undefined;
  }, [valuePrimary]);
  
  /** Convert secondary value to string for display */
  const valueSecondaryString = useMemo(() => {
    return valueSecondary !== undefined ? valueSecondary.toString() : undefined;
  }, [valueSecondary]);

  /**
   * Handle card click event, toggling selection state if not disabled;
   * also calls any additional onClick handler provided via props.
   */
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!disabled) {
      onSelect?.(!selected);
      onClick?.(event);
    }
  };

  /**
   * Handle key down event for accessibility, toggling selection state if not
   * disabled when Enter or Space key is pressed.
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onSelect?.(!selected);
    }
  };

  /**
   * Compute dynamic class names based on props and state
   */
  const classNames = cn(
    selectableCardVariants({ variant }),
    {
      'cursor-not-allowed opacity-50': disabled,
    },
    className,
  );

  return (
    <div
      aria-disabled={disabled}
      aria-pressed={selected}
      className={classNames}
      data-selected={selected}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={disabled ? -1 : 0}
      {...props}
    >
      {/* Icon */}
      <div className={cardIconContainerVariants({ variant })} data-selected={selected}>
        <Icon name={icon} size={24} className={cardIconVariants({ variant })} weight="fill" />
      </div>

      {/* Text Content */}
      <div className="pointer-events-none flex min-w-0 flex-1 flex-col">
        {label && (
          <TextContent
            as="span"
            className="mb-0.5"
            color="secondary"
            label={label}
            type="paragraph"
            variant="bodySmall"
          />
        )}
        {text && (
          <TextContent
            as="span"
            color="primary"
            label={text}
            type="paragraph"
            variant="bodyMedium"
          />
        )}
      </div>

      {/* Value Display */}
      {(valueString || (valuePrimaryString && valueSecondaryString)) && (
        <div className="pointer-events-none min-w-24 flex-shrink-0 text-left">
          {valueString ? (
            <span className="typography-page-title" data-key="value">
              {valueString}
            </span>
          ) : (
            <div className="flex items-baseline">
              <span className="typography-page-title" data-key="value-primary">
                {valuePrimaryString}
              </span>
              <span className="typography-section-title">
                <span data-key="delimiter">{delimiter}</span>
                <span data-key="value-secondary">{valueSecondaryString}</span>
              </span>
            </div>
          )}
        </div>
      )}

      {/* Selection Indicator */}
      <div className="pointer-events-none absolute right-4 top-4">
        {selected ? (
          <Icon
            name="CheckCircle"
            size={24}
            className={checkIconVariants({ variant })}
            weight="bold"
          />
        ) : (
          <Icon
            name="Circle"
            size={24}
            className={checkIconVariants({ variant: 'unselected' })}
            weight="bold"
          />
        )}
      </div>
    </div>
  );
};

SelectableCard.displayName = 'SelectableCard';

export default SelectableCard;
